import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  formatarValor,
  hojeEmBrasilia,
  lerValor,
  useApagarDespesa,
  useDespesasDoChamado,
  useLancarDespesa,
} from "../../nucleo/horas";
import { caminhoDoAnexoDoTicket } from "../../nucleo/tickets";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";

interface Props {
  ticketId: string;
  podeGerenciar: boolean;
}

function quandoFoi(dia: string): string {
  const [ano, mes, diaDoMes] = dia.split("-");
  return `${diaDoMes}/${mes}/${ano}`;
}

/** Despesas do chamado (só a equipe), com o comprovante guardado como anexo interno. */
export function DespesasDoChamado({ ticketId, podeGerenciar }: Props) {
  const despesas = useDespesasDoChamado(ticketId);
  const lancar = useLancarDespesa(ticketId);
  const apagar = useApagarDespesa(ticketId);
  const [valor, setValor] = useState("");
  const [descricao, setDescricao] = useState("");
  const [dia, setDia] = useState(hojeEmBrasilia());
  const [comprovante, setComprovante] = useState<File | null>(null);
  const [erroDoValor, setErroDoValor] = useState<string | undefined>();
  const arquivo = useRef<HTMLInputElement>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const centavos = lerValor(valor);
    if (centavos === null) {
      setErroDoValor("Informe o valor em reais, como 125,50.");
      return;
    }
    if (!descricao.trim()) return;
    setErroDoValor(undefined);
    lancar.mutate(
      { valor_centavos: centavos, descricao: descricao.trim(), dia, comprovante },
      {
        onSuccess: () => {
          setValor("");
          setDescricao("");
          setComprovante(null);
          if (arquivo.current) arquivo.current.value = "";
        },
      },
    );
  }

  const erro = lancar.error ?? apagar.error;

  return (
    <Cartao titulo="Despesas" className="mb-4" data-testid="despesas-do-chamado">
      <p className="nota-rodape mb-3">
        Só a equipe vê as despesas e os comprovantes. PDF, imagem, planilha ou ZIP, até 25 MB.
      </p>
      {despesas.isPending ? (
        <p role="status">Carregando despesas…</p>
      ) : despesas.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(despesas.error)}</Aviso>
      ) : (
        <>
          <p className="cartao__texto mb-3" data-testid="total-de-despesas">
            Total lançado: <strong>{formatarValor(despesas.data.total_centavos)}</strong>
          </p>
          {despesas.data.despesas.length === 0 ? (
            <p className="nota-rodape mb-3">Nenhuma despesa lançada neste chamado.</p>
          ) : (
            <ul className="lista-de-itens mb-3" data-testid="lista-de-despesas">
              {despesas.data.despesas.map((item) => (
                <li className="flex flex-wrap items-center gap-2" key={item.id}>
                  <span>
                    <strong>{formatarValor(item.valor_centavos)}</strong> · {item.descricao} ·{" "}
                    {item.agente_nome} · {quandoFoi(item.dia)}
                  </span>
                  {item.anexo_id ? (
                    <a href={caminhoDoAnexoDoTicket(ticketId, item.anexo_id)} download>
                      Comprovante
                    </a>
                  ) : (
                    <Chip estado="aguardando" semPonto>Sem comprovante</Chip>
                  )}
                  {item.meu || podeGerenciar ? (
                    <Botao
                      variante="fantasma"
                      tamanho="pequeno"
                      aria-label={`Apagar despesa ${item.descricao}`}
                      disabled={apagar.isPending}
                      onClick={() => apagar.mutate(item.id)}
                    >
                      Apagar
                    </Botao>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <form className="flex flex-wrap items-end gap-3" onSubmit={enviar} data-testid="lancar-despesa">
            <Campo
              rotulo="Valor (R$)"
              value={valor}
              onChange={(evento) => setValor(evento.target.value)}
              erro={erroDoValor}
              inputMode="decimal"
            />
            <Campo
              rotulo="Descrição"
              value={descricao}
              onChange={(evento) => setDescricao(evento.target.value)}
              classeDaMoldura="flex-1"
              maxLength={300}
            />
            <Campo
              rotulo="Dia"
              type="date"
              value={dia}
              max={hojeEmBrasilia()}
              onChange={(evento) => setDia(evento.target.value)}
            />
            <Campo
              rotulo="Comprovante"
              type="file"
              ref={arquivo}
              onChange={(evento) => setComprovante(evento.target.files?.[0] ?? null)}
            />
            <Botao type="submit" disabled={lancar.isPending || !descricao.trim()}>
              {lancar.isPending ? "Lançando…" : "Lançar despesa"}
            </Botao>
          </form>
          {erro ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(erro)}</Aviso> : null}
        </>
      )}
    </Cartao>
  );
}
